/**
 * Training workspace.
 *
 * Centers the org's primary training session and lets new knowledge roll into it over time.
 */

import React, { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  ChevronDown,
  Copy,
  ExternalLink,
  GraduationCap,
  Globe,
  Loader2,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import type { Interviewer } from '@/lib/api/onboarding';
import {
  ContentSelector,
  KnowledgeAssetSelector,
} from '@/components/onboarding/KnowledgeSourceSelectors';

/* ------------------------------------------------------------------ */
/*  Animation variants                                                */
/* ------------------------------------------------------------------ */

const collapseVariants = {
  open: {
    height: 'auto',
    opacity: 1,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
  },
};

/* ------------------------------------------------------------------ */
/*  Training Session Card                                             */
/* ------------------------------------------------------------------ */

const InterviewerCard: React.FC<{
  interviewer: Interviewer;
  onCopy: (interviewer: Interviewer) => void;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
  isPrimary?: boolean;
}> = ({ interviewer, onCopy, onDelete, isDeleting = false, isPrimary = false }) => {
  const navigate = useNavigate();
  const link =
    interviewer.interview_link || `${window.location.origin}/interview/${interviewer.token}`;

  return (
    <Card
      className={`cursor-pointer transition-colors duration-200 ${
        isPrimary
          ? 'hover:border-primary/30 border-border'
          : 'hover:border-border/80 border-border/50'
      }`}
      onClick={() => navigate(`/training/${interviewer.id}`)}
    >
      <CardContent className={isPrimary ? 'p-5' : 'p-4'}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <GraduationCap className={`shrink-0 text-primary ${isPrimary ? 'h-4 w-4' : 'h-3.5 w-3.5'}`} />
              <h3 className={`font-medium truncate ${isPrimary ? 'text-sm' : 'text-xs'}`}>
                {isPrimary ? 'Primary Training Workspace' : interviewer.name || 'Training Workspace'}
              </h3>
              <Badge
                variant={interviewer.processing_status === 'completed' ? 'default' : 'secondary'}
                className="text-[10px] shrink-0"
              >
                {interviewer.processing_status === 'completed' ? 'Ready' : interviewer.processing_status}
              </Badge>
              {isPrimary && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  Primary
                </Badge>
              )}
            </div>

            {isPrimary && (
              <p className="text-xs text-muted-foreground mt-1.5">
                All new knowledge updates this workspace.
              </p>
            )}

            <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
              {interviewer.website_url && (
                <span className="flex items-center gap-1 truncate max-w-[180px]">
                  <Globe className="h-3 w-3 shrink-0" />
                  {interviewer.website_url}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {interviewer.session_count} session{interviewer.session_count !== 1 ? 's' : ''}
              </span>
              <span>{new Date(interviewer.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onCopy(interviewer)}
              title="Copy link"
            >
              <Copy className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => window.open(link, '_blank')}
              title="Open link"
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={() => onDelete(interviewer.id)}
                disabled={isDeleting}
                title="Delete"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 ml-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Content                                                      */
/* ------------------------------------------------------------------ */

export const InterviewerLinksContent: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  const {
    interviewers,
    primaryInterviewer,
    isLoading,
    isCreating,
    isConsolidating,
    isDeleting,
    createInterviewer,
    consolidateInterviewers,
    deleteInterviewer,
    copyLink,
    uploadFile,
  } = useInterviewerSetup(orgId);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [knowledgeBaseUrl, setKnowledgeBaseUrl] = useState('');
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [selectedKnowledgeAssetIds, setSelectedKnowledgeAssetIds] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [formExpanded, setFormExpanded] = useState(!primaryInterviewer);
  const [showLegacy, setShowLegacy] = useState(false);
  const [showConsolidateConfirm, setShowConsolidateConfirm] = useState(false);

  // Expand form by default when no primary exists
  React.useEffect(() => {
    if (!primaryInterviewer) setFormExpanded(true);
  }, [primaryInterviewer]);

  const hasSources =
    !!websiteUrl.trim() ||
    !!knowledgeBaseUrl.trim() ||
    selectedVideoIds.length > 0 ||
    selectedKnowledgeAssetIds.length > 0 ||
    selectedFiles.length > 0;

  const handleCreate = useCallback(async () => {
    setIsUploadingFiles(true);

    let fileUploads;
    if (selectedFiles.length > 0) {
      const uploaded = await Promise.all(selectedFiles.map((file) => uploadFile(file)));
      if (uploaded.some((file) => !file)) {
        setIsUploadingFiles(false);
        return;
      }
      fileUploads = uploaded.filter(Boolean);
    }

    setIsUploadingFiles(false);

    const result = await createInterviewer(
      websiteUrl.trim() || undefined,
      knowledgeBaseUrl.trim() || undefined,
      fileUploads,
      name.trim() || undefined,
      selectedVideoIds.length > 0 ? selectedVideoIds : undefined,
      selectedKnowledgeAssetIds.length > 0 ? selectedKnowledgeAssetIds : undefined,
    );
    if (result) {
      setName('');
      setWebsiteUrl('');
      setKnowledgeBaseUrl('');
      setSelectedVideoIds([]);
      setSelectedKnowledgeAssetIds([]);
      setSelectedFiles([]);
      navigate(`/training/${result.id}`);
    }
  }, [
    createInterviewer,
    knowledgeBaseUrl,
    navigate,
    name,
    selectedFiles,
    selectedKnowledgeAssetIds,
    selectedVideoIds,
    uploadFile,
    websiteUrl,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate],
  );

  const legacyInterviewers = interviewers.filter(
    (i) => !primaryInterviewer || i.id !== primaryInterviewer.id,
  );

  const handleConsolidateLegacy = useCallback(async () => {
    if (!primaryInterviewer || legacyInterviewers.length === 0 || isConsolidating) return;
    setShowConsolidateConfirm(true);
  }, [isConsolidating, legacyInterviewers.length, primaryInterviewer]);

  const confirmConsolidate = useCallback(async () => {
    if (!primaryInterviewer || legacyInterviewers.length === 0) return;
    setShowConsolidateConfirm(false);
    await consolidateInterviewers(
      primaryInterviewer.id,
      legacyInterviewers.map((i) => i.id),
    );
  }, [consolidateInterviewers, legacyInterviewers, primaryInterviewer]);

  if (!orgId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Select an organization to continue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---- Primary Workspace ---- */}
      {primaryInterviewer && (
        <InterviewerCard interviewer={primaryInterviewer} onCopy={copyLink} isPrimary />
      )}

      {/* ---- Add Knowledge ---- */}
      <Card>
        <CardContent className="p-0">
          {primaryInterviewer ? (
            <>
              <button
                type="button"
                className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors rounded-t-xl"
                onClick={() => setFormExpanded((v) => !v)}
              >
                <div className="flex items-center gap-2">
                  <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">Add knowledge</span>
                </div>
                <motion.div
                  animate={{ rotate: formExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {formExpanded && (
                  <motion.div
                    key="form"
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={collapseVariants}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-border/60 p-4 space-y-4">
                      <KnowledgeFormFields
                        showName={false}
                        name={name}
                        setName={setName}
                        websiteUrl={websiteUrl}
                        setWebsiteUrl={setWebsiteUrl}
                        knowledgeBaseUrl={knowledgeBaseUrl}
                        setKnowledgeBaseUrl={setKnowledgeBaseUrl}
                        selectedFiles={selectedFiles}
                        setSelectedFiles={setSelectedFiles}
                        selectedKnowledgeAssetIds={selectedKnowledgeAssetIds}
                        setSelectedKnowledgeAssetIds={setSelectedKnowledgeAssetIds}
                        selectedVideoIds={selectedVideoIds}
                        setSelectedVideoIds={setSelectedVideoIds}
                        orgId={orgId}
                        disabled={isCreating || isUploadingFiles}
                        onKeyDown={handleKeyDown}
                      />
                      <Button
                        onClick={handleCreate}
                        disabled={isCreating || isUploadingFiles || !hasSources}
                        className="w-full sm:w-auto"
                      >
                        {isCreating || isUploadingFiles ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing…
                          </>
                        ) : (
                          'Update Training'
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            <div className="p-4 space-y-4">
              <CardTitle className="text-base">Set up primary training</CardTitle>
              <KnowledgeFormFields
                showName
                name={name}
                setName={setName}
                websiteUrl={websiteUrl}
                setWebsiteUrl={setWebsiteUrl}
                knowledgeBaseUrl={knowledgeBaseUrl}
                setKnowledgeBaseUrl={setKnowledgeBaseUrl}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                selectedKnowledgeAssetIds={selectedKnowledgeAssetIds}
                setSelectedKnowledgeAssetIds={setSelectedKnowledgeAssetIds}
                selectedVideoIds={selectedVideoIds}
                setSelectedVideoIds={setSelectedVideoIds}
                orgId={orgId}
                disabled={isCreating || isUploadingFiles}
                onKeyDown={handleKeyDown}
              />
              <Button
                onClick={handleCreate}
                disabled={isCreating || isUploadingFiles || !hasSources}
                className="w-full sm:w-auto"
              >
                {isCreating || isUploadingFiles ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing…
                  </>
                ) : (
                  'Create Primary Training'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- Loading / Empty ---- */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !primaryInterviewer ? (
        <div className="text-center py-12 text-muted-foreground">
          <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No primary training yet.</p>
        </div>
      ) : legacyInterviewers.length > 0 ? (
        /* ---- Legacy Sessions ---- */
        <div className="space-y-2">
          <button
            type="button"
            className="flex items-center justify-between w-full text-left py-1"
            onClick={() => setShowLegacy((v) => !v)}
          >
            <span className="text-xs font-medium text-muted-foreground">
              {legacyInterviewers.length} older session{legacyInterviewers.length !== 1 ? 's' : ''}
            </span>
            <motion.div
              animate={{ rotate: showLegacy ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {showLegacy && (
              <motion.div
                key="legacy"
                initial="closed"
                animate="open"
                exit="closed"
                variants={collapseVariants}
                className="overflow-hidden space-y-2"
              >
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={handleConsolidateLegacy}
                    disabled={isConsolidating}
                  >
                    {isConsolidating && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                    Consolidate into primary
                  </Button>
                </div>
                {legacyInterviewers.map((interviewer) => (
                  <InterviewerCard
                    key={interviewer.id}
                    interviewer={interviewer}
                    onCopy={copyLink}
                    onDelete={deleteInterviewer}
                    isDeleting={isDeleting}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : null}

      {/* Consolidate Confirmation */}
      <AlertDialog open={showConsolidateConfirm} onOpenChange={setShowConsolidateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge into primary workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              Merge {legacyInterviewers.length} older session{legacyInterviewers.length !== 1 ? 's' : ''} into the primary workspace. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmConsolidate}>
              Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared Form Fields                                                */
/* ------------------------------------------------------------------ */

const KnowledgeFormFields: React.FC<{
  showName: boolean;
  name: string;
  setName: (v: string) => void;
  websiteUrl: string;
  setWebsiteUrl: (v: string) => void;
  knowledgeBaseUrl: string;
  setKnowledgeBaseUrl: (v: string) => void;
  selectedFiles: File[];
  setSelectedFiles: (v: File[]) => void;
  selectedKnowledgeAssetIds: string[];
  setSelectedKnowledgeAssetIds: (v: string[]) => void;
  selectedVideoIds: string[];
  setSelectedVideoIds: (v: string[]) => void;
  orgId: string;
  disabled: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
}> = ({
  showName,
  name,
  setName,
  websiteUrl,
  setWebsiteUrl,
  knowledgeBaseUrl,
  setKnowledgeBaseUrl,
  selectedFiles,
  setSelectedFiles,
  selectedKnowledgeAssetIds,
  setSelectedKnowledgeAssetIds,
  selectedVideoIds,
  setSelectedVideoIds,
  orgId,
  disabled,
  onKeyDown,
}) => (
  <>
    {showName && (
      <div className="space-y-1.5">
        <Label htmlFor="session-name" className="text-xs">
          Name
        </Label>
        <Input
          id="session-name"
          type="text"
          placeholder="e.g., Main product training"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className="h-9"
        />
      </div>
    )}

    <div className="grid gap-3 md:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="website-url" className="text-xs">
          Website URL
        </Label>
        <Input
          id="website-url"
          type="url"
          placeholder="https://example.com"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          disabled={disabled}
          className="h-9"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="knowledge-base-url" className="text-xs">
          Knowledge Base URL
        </Label>
        <Input
          id="knowledge-base-url"
          type="url"
          placeholder="https://docs.example.com"
          value={knowledgeBaseUrl}
          onChange={(e) => setKnowledgeBaseUrl(e.target.value)}
          disabled={disabled}
          className="h-9"
        />
      </div>
    </div>

    <div className="space-y-1.5">
      <Label htmlFor="training-files" className="text-xs">
        Upload Files
      </Label>
      <Input
        id="training-files"
        type="file"
        multiple
        accept=".pdf,video/*"
        onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
        disabled={disabled}
        className="h-9"
      />
      {selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {selectedFiles.map((file) => (
            <span
              key={`${file.name}-${file.size}`}
              className="rounded-md border bg-muted/50 px-2 py-0.5 text-[10px]"
            >
              {file.name}
            </span>
          ))}
        </div>
      )}
    </div>

    <KnowledgeAssetSelector
      orgId={orgId}
      selectedAssetIds={selectedKnowledgeAssetIds}
      onSelectionChange={setSelectedKnowledgeAssetIds}
      disabled={disabled}
    />

    <ContentSelector
      orgId={orgId}
      selectedVideoIds={selectedVideoIds}
      onSelectionChange={setSelectedVideoIds}
      disabled={disabled}
    />
  </>
);

export default InterviewerLinksContent;
